// @ts-nocheck
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@store/MobiusOverrides/Clickable/Clickable";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, HTMLAttributes, KeyboardEvent, KeyboardEventHandler, MouseEvent, useRef, useState } from "react";
import { css } from "styled-components";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { focusStyles } from "@toolkit/Themes/common/mixins";

export interface TabModel {
    label: string;
    iconUrl?: string;
    isDefault?: boolean;
    clickableProps?: ClickableProps;
    onActivate?: () => void;
}

export interface SegmentedToggleProps extends HTMLAttributes<HTMLElement> {
    tabs: TabModel[];
    onChangeIndex?: (index: number) => void;
    pattern?: "tab" | "radio";
}

export interface SegmentedToggleStyles {
    wrapper?: InjectableCss;
    tab?: ClickablePresentationProps;
    tabIcon?: InjectableCss;
}

export const segmentedToggleStyles: SegmentedToggleStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: stretch;
            padding: ${pxToRem(4)};
            border-radius: ${pxToRem(1000)};
            background-color: ${getColor("common.disabled")};
            flex-shrink: 0;

            ${breakpoint("md")} {
                width: auto;
            }
        `,
    },
    tab: {
        css: css`
            ${focusStyles()}
            justify-content: center;
            gap: ${pxToRem(4)};
            padding: ${pxToRem(10)} ${pxToRem(16)};
            border-radius: ${pxToRem(100)};
            color: ${getColor("text.light")};
            flex: auto;
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: 1.4;
            word-break: break-word;

            ${transitionHoverStyles("color", "background-color")};

            &[aria-selected="true"],
            &[aria-checked="true"] {
                background-color: ${getColor("primary")};
                color: ${getColor("primary.contrast")};
            }

            &[aria-selected="false"]:hover,
            &[aria-checked="false"]:hover {
                color: ${getColor("primary")};
            }

            ${breakpoint("sm")} {
                font-size: ${x => x.theme.design.typography.size.base};
            }
        `,
    },
    tabIcon: {
        css: css`
            display: inline-block;
            width: ${pxToRem(24)};
            height: ${pxToRem(24)};
            flex-shrink: 0;
            background-color: currentColor;
            /* noinspection CssUnresolvedCustomProperty */
            mask-image: var(--icon-url);
            mask-size: contain;
            mask-repeat: no-repeat;
            mask-position: center;
        `,
    },
};

const styles = segmentedToggleStyles;
const SpanWrapper = getStyledWrapper("span");

const SegmentedToggle: FC<SegmentedToggleProps> = ({ tabs, onChangeIndex, pattern = "tab", ...htmlAttributes }) => {
    const segmentRefs = useRef<(HTMLButtonElement | HTMLAnchorElement | HTMLLinkElement | null)[]>([]);
    const [activeIndex, setActiveIndex] = useState(() => {
        const defaultTabIndex = tabs.findIndex(tab => tab.isDefault);

        return defaultTabIndex >= 0 ? defaultTabIndex : 0;
    });

    const setActiveTab = (nextIndex: number, moveFocus = false) => {
        if (activeIndex === nextIndex) {
            return;
        }

        setActiveIndex(nextIndex);
        onChangeIndex?.(nextIndex);
        tabs[nextIndex].onActivate?.();

        if (!moveFocus) {
            return;
        }

        segmentRefs.current[nextIndex]?.focus();
    };

    const handleTabKeyDown: KeyboardEventHandler<HTMLButtonElement> = event => {
        if (event.key === "ArrowRight") {
            event.preventDefault();
            if (activeIndex < tabs.length - 1) {
                setActiveTab(activeIndex + 1, true);
            }
        } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            if (activeIndex > 0) {
                setActiveTab(activeIndex - 1, true);
            }
        } else if (event.key === "Home") {
            event.preventDefault();
            setActiveTab(0, true);
        } else if (event.key === "End") {
            event.preventDefault();
            setActiveTab(tabs.length - 1, true);
        }
    };

    const isRadio = pattern === "radio";

    return (
        <StyledWrapper
            {...styles.wrapper}
            role={isRadio ? "radiogroup" : "tablist"}
            aria-label={translate("Toggle content view")}
            {...htmlAttributes}
        >
            {tabs.map((tab, index) => {
                const { label, iconUrl, clickableProps } = tab;

                return (
                    <Clickable
                        {...styles.tab}
                        key={label}
                        role={isRadio ? "radio" : "tab"}
                        type="button"
                        {...clickableProps}
                        ref={el => {
                            segmentRefs.current[index] = el;
                        }}
                        aria-selected={isRadio ? undefined : activeIndex === index}
                        aria-checked={isRadio ? activeIndex === index : undefined}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            clickableProps?.onClick?.(event);
                            setActiveTab(index);
                        }}
                        onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                            clickableProps?.onKeyDown?.(event);
                            handleTabKeyDown(event);
                        }}
                        tabIndex={activeIndex === index ? 0 : -1}
                    >
                        {iconUrl && (
                            <SpanWrapper
                                {...styles.tabIcon}
                                style={
                                    {
                                        "--icon-url": `url(${iconUrl})`,
                                    } as React.CSSProperties
                                }
                                aria-hidden="true"
                            />
                        )}
                        <span>{label}</span>
                    </Clickable>
                );
            })}
        </StyledWrapper>
    );
};

export default SegmentedToggle;
